import React from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useUpdateNodeConfig, useUploadNodeLogo } from '../../hooks/useNode';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { injectNodeTheme } from '../../utils/theme';
import { 
  Palette, 
  Upload, 
  Image as ImageIcon, 
  Globe, 
  Eye,
  Bell,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

export const BrandingPage = () => {
  const { node, brand } = useTenant();
  const updateMutation = useUpdateNodeConfig(node?.id || '');
  const uploadLogoMutation = useUploadNodeLogo(node?.id || '');

  const [primaryColor, setPrimaryColor] = React.useState(node?.config?.primaryColor || '#0F1F3D');
  const [secondaryColor, setSecondaryColor] = React.useState(node?.config?.secondaryColor || '#1A56A0');
  const [logoUrl, setLogoUrl] = React.useState(node?.config?.logoUrl || '');
  const [fontFamily, setFontFamily] = React.useState(node?.config?.fontFamily || 'sans');
  const [roundedCorners, setRoundedCorners] = React.useState(node?.config?.roundedCorners || 'lg');
  const [themeId, setThemeId] = React.useState(node?.config?.themeId || 'bauhaus');

  // Sync local fields from node.config when it changes (render-phase adjustment).
  const brandConfig = node?.config;
  const [prevBrandConfig, setPrevBrandConfig] = React.useState<typeof brandConfig | null>(null);
  if (brandConfig && prevBrandConfig !== brandConfig) {
    setPrevBrandConfig(brandConfig);
    if (brandConfig.primaryColor) setPrimaryColor(brandConfig.primaryColor);
    if (brandConfig.secondaryColor) setSecondaryColor(brandConfig.secondaryColor);
    if (brandConfig.logoUrl) setLogoUrl(brandConfig.logoUrl);
    if (brandConfig.fontFamily) setFontFamily(brandConfig.fontFamily);
    if (brandConfig.roundedCorners) setRoundedCorners(brandConfig.roundedCorners);
    if (brandConfig.themeId) setThemeId(brandConfig.themeId);
  }

  // Live preview effect
  React.useEffect(() => {
    injectNodeTheme({
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      logoUrl: logoUrl,
      fontFamily: fontFamily,
      roundedCorners: roundedCorners,
      themeId: themeId
    });
  }, [primaryColor, secondaryColor, logoUrl, fontFamily, roundedCorners, themeId]);

  const handleSave = () => {
    updateMutation.mutate({
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      logoUrl: logoUrl,
      fontFamily: fontFamily,
      roundedCorners: roundedCorners,
      themeId: themeId
    }, {
      onSuccess: () => toast.success('Branding saved successfully'),
      onError: () => toast.error('Failed to save branding')
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadLogoMutation.mutate(file, {
      onSuccess: (data) => {
        setLogoUrl(data.url);
        toast.success('Logo uploaded');
      },
      onError: () => toast.error('Failed to upload logo')
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Branding</h1>
          <p className="text-xs md:text-sm text-gray-500">Customize your organization's visual identity.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Controls */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette size={18} className="text-blue-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Colors</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Primary Color</label>
                <div className="flex gap-3">
                  <input 
                    type="color" 
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-10 h-10 rounded-none cursor-pointer border-none p-0"
                  />
                  <Input 
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Secondary Color</label>
                <div className="flex gap-3">
                  <input 
                    type="color" 
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-10 h-10 rounded-none cursor-pointer border-none p-0"
                  />
                  <Input 
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Visual Theme</label>
                <select 
                  value={themeId}
                  onChange={(e) => setThemeId(e.target.value as any)}
                  className="w-full bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
                >
                  <option value="bauhaus">Bauhaus (Default)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Typography Setup</label>
                <select 
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value as any)}
                  className="w-full bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
                >
                  <option value="sans">Modern Sans (DM Sans / Inter)</option>
                  <option value="serif">System Serif (Charter / Georgia)</option>
                  <option value="display">Editorial Serif (DM Serif / Playfair)</option>
                  <option value="mono">Technical Mono (JetBrains / Fira)</option>
                  <option value="grotesk">Swiss Grotesk (Space Grotesk)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Corner Style</label>
                <select 
                  value={roundedCorners}
                  onChange={(e) => setRoundedCorners(e.target.value as any)}
                  className="w-full bg-white border-2 border-black dark:border-white rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 dark:bg-slate-900"
                >
                  <option value="none">Sharp (None)</option>
                  <option value="sm">Subtle (Sm)</option>
                  <option value="md">Balanced (Md)</option>
                  <option value="lg">Soft (Lg)</option>
                  <option value="full">Round (Full)</option>
                </select>
              </div>
            </div>

            <div className="pt-4 border-t border-black dark:border-white">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon size={18} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Assets</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Organization Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-none flex items-center justify-center border-2 border-black dark:border-white overflow-hidden dark:bg-slate-800">
                      {logoUrl ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full" /> : <ImageIcon className="text-gray-300" />}
                    </div>
                    <label className="flex-1">
                      <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-black dark:border-white rounded-none text-xs font-semibold text-gray-500 hover:bg-white cursor-pointer transition-colors dark:bg-slate-950">
                        <Upload size={14} />
                        Upload Logo
                      </div>
                      <input type="file" className="hidden" onChange={handleLogoUpload} accept="image/*" />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-black dark:border-white">
              <div className="flex items-center gap-2 mb-4">
                <Globe size={18} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Domain</h3>
              </div>
              <Input 
                label="Subdomain"
                value={node?.slug || ''}
                readOnly
                disabled
                className="bg-white font-mono text-gray-500 dark:bg-slate-950"
              />
              <p className="text-[10px] text-gray-400 mt-1 italic">Subdomain changes require manual support.</p>
            </div>

            <Button 
              className="w-full" 
              onClick={handleSave}
              isLoading={updateMutation.isPending}
            >
              Save Branding
            </Button>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <Eye size={18} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-widest font-mono">Live Preview</h3>
          </div>

          <div className="space-y-8">
            {/* Navbar Preview */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400">Navbar</p>
              <div 
                className="h-14 rounded-none flex items-center px-4 gap-3 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
                style={{ backgroundColor: primaryColor }}
              >
                <span className="text-white font-serif text-lg">{brand?.name}</span>
                <div className="w-px h-5 bg-white/20 dark:bg-slate-900" />
                <span className="text-sm font-medium" style={{ color: `${secondaryColor}33` }}>{node?.name}</span>
                <div className="flex-1" />
                <div className="w-8 h-8 rounded-none bg-white/10 flex items-center justify-center text-white dark:bg-slate-900">
                  <Bell size={18} />
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-black dark:border-white border-white/20 rounded-none text-[10px] font-bold text-white/80">
                  Switch Org
                  <ChevronDown size={12} />
                </div>
                <div className="w-8 h-8 rounded-none bg-blue-500 border-2 border-white/20" />
              </div>
            </div>

            {/* Sidebar/Button Preview */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400">Components</p>
              <div className="bg-white border-2 border-black dark:border-white rounded-none p-8 flex flex-wrap gap-6 items-center dark:bg-slate-900">
                <Button style={{ backgroundColor: primaryColor }}>Primary Button</Button>
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-none text-sm font-medium" style={{ backgroundColor: `${secondaryColor}15`, color: secondaryColor }}>
                  Active Nav Item
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-none" style={{ backgroundColor: secondaryColor }} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Status Indicator</span>
                </div>
              </div>
            </div>

            {/* Logo Preview */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400">Logo Presentation</p>
              <div className="bg-navy-900 rounded-none p-12 flex items-center justify-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo Preview" className="max-h-24" />
                ) : (
                  <div className="text-white/20 flex flex-col items-center gap-2">
                    <ImageIcon size={48} />
                    <span className="text-xs font-mono uppercase tracking-widest">No Logo Uploaded</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
